import { Button } from "@hortis/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@hortis/ui/dialog";
import * as E from "fp-ts/Either";
import * as TE from "fp-ts/TaskEither";
import { pipe } from "fp-ts/function";
import { namedOperations } from "generated/graphql";
import { useMemo } from "react";
import { queryClient } from "src/app";
import { type UseQueryExecute } from "urql";
import { reinstateAccession } from "../../fetchers";
import {
  onUpdateAccessionFailure,
  onUpdateAccessionSuccess,
} from "../edit-drawer/utils";

const reinstateTask = TE.tryCatchK(reinstateAccession, E.toError);

interface ReinstateDialogProps {
  accessionId: string;
  accessToken: string;
  refetch?: UseQueryExecute;
  onOpenChange: (open: boolean) => void;
}

export const ReinstateDialogContent = ({
  accessionId,
  accessToken,
  refetch,
  onOpenChange,
}: ReinstateDialogProps) => {
  const onSubmit = useMemo(
    () => async () => {
      await pipe(
        reinstateTask(accessToken, { accessionId }),
        TE.match(
          onUpdateAccessionFailure,
          onUpdateAccessionSuccess("Accession reinstated", refetch ?? null),
        ),
      )();

      queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccession],
      });

      onOpenChange(false);
    },
    [accessToken, accessionId, onOpenChange, refetch],
  );

  return (
    <DialogContent data-cy="reinstate-dialog">
      <DialogHeader>
        <DialogTitle>Reinstate accession?</DialogTitle>
        <DialogDescription>
          Reinstating an accession will return it to the collection. The status
          of any associated plant materials will not change.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          onClick={() => {
            onOpenChange(false);
          }}
          testId="cancel-reinstate-dialog"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={onSubmit}
          data-cy="submit-reinstate-dialog"
        >
          Reinstate
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
